import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SWIPE_THRESHOLD = 60; // px distance to trigger slide change
const SWIPE_VELOCITY = 300; // px/s velocity shortcut to trigger slide change

export const HeroCarousel = () => {
  const [[currentIndex, direction], setPage] = useState([0, 0]);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch carousel images from settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        const heroImages = response.data.hero_images;

        if (heroImages && heroImages.length > 0) {
          const activeImages = heroImages
            .filter((img) => img.active && img.url)
            .map((img) => ({ ...img, url: getImageUrl(img.url) }));
          if (activeImages.length > 0) {
            setCarouselImages(activeImages);
          }
        }
      } catch (error) {
        console.error('Error fetching carousel settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const paginate = useCallback(
    (newDirection) => {
      setPage(([prev]) => {
        const total = carouselImages.length;
        if (total === 0) return [0, 0];
        const next = (prev + newDirection + total) % total;
        return [next, newDirection];
      });
    },
    [carouselImages.length]
  );

  const goToSlide = (index) => {
    setPage(([prev]) => [index, index > prev ? 1 : -1]);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying || carouselImages.length <= 1) return;
    const interval = setInterval(() => paginate(1), 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, paginate, carouselImages.length]);

  // Reset index if it's out of bounds
  useEffect(() => {
    if (currentIndex >= carouselImages.length && carouselImages.length > 0) {
      setPage([0, 0]);
    }
  }, [carouselImages.length, currentIndex]);

  const currentSlide = carouselImages[currentIndex] || carouselImages[0];

  // Slide animation variants — supports drag offset via custom
  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? '100%' : dir < 0 ? '-100%' : 0,
      opacity: dir === 0 ? 0 : 1,
      scale: dir === 0 ? 1.05 : 1,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? '-100%' : dir < 0 ? '100%' : 0,
      opacity: dir === 0 ? 0 : 1,
      scale: 1,
    }),
  };

  const handleDragEnd = (_event, info) => {
    const { offset, velocity } = info;
    const swipe = Math.abs(offset.x) > SWIPE_THRESHOLD || Math.abs(velocity.x) > SWIPE_VELOCITY;
    if (!swipe) {
      // snap back; resume autoplay
      setTimeout(() => setIsAutoPlaying(true), 3000);
      return;
    }
    if (offset.x < 0) {
      paginate(1);
    } else {
      paginate(-1);
    }
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Show loading state while fetching images
  if (loading || carouselImages.length === 0) {
    return (
      <section
        className="relative h-[75vh] min-h-[550px] max-h-[800px] overflow-hidden bg-[#1a1a1a]"
        data-testid="hero-carousel"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/60 font-['Poppins']">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!currentSlide) {
    return null;
  }

  const multipleSlides = carouselImages.length > 1;

  return (
    <section
      className="relative h-[75vh] min-h-[550px] max-h-[800px] overflow-hidden select-none bg-[#1a1a1a]"
      data-testid="hero-carousel"
    >
      {/* Draggable Background Image Layer */}
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.div
          key={currentIndex}
          className="absolute inset-0"
          style={{ cursor: multipleSlides ? 'grab' : 'default', touchAction: 'pan-y' }}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 260, damping: 32 },
            opacity: { duration: 0.5 },
            scale: { duration: 0.6 },
          }}
          drag={multipleSlides ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.25}
          onDragStart={() => setIsAutoPlaying(false)}
          onDragEnd={handleDragEnd}
          whileDrag={{ cursor: 'grabbing' }}
          data-testid="hero-slide"
        >
          <img
            src={currentSlide.url}
            alt={currentSlide.title || 'Mad Brothers Bakery'}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover pointer-events-none"
            draggable="false"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/85 via-[#1a1a1a]/50 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/60 via-transparent to-transparent pointer-events-none" />
        </motion.div>
      </AnimatePresence>

      {/* Content (non-interactive pass-through for drag) */}
      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center pointer-events-none">
        <div className="max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={`text-${currentIndex}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="font-handwritten text-2xl sm:text-3xl text-[#D4AF37]">
                Est. 2021 • Dublin 7
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3 leading-tight">
                {currentSlide.title || 'Mad Brothers Bakery'}
              </h1>
              <p className="text-white/90 text-lg sm:text-xl mt-4">
                {currentSlide.subtitle || 'Getting mad never felt so good.'}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* CTA Buttons — re-enable pointer events on interactive elements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-4 mt-8 pointer-events-auto"
          >
            <a
              href="/shop"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-[#1a1a1a] font-['Poppins'] font-semibold rounded-full hover:bg-white/90 transition-all hover:scale-105 btn-shine"
              data-testid="hero-shop-btn"
            >
              Shop Now
              <ChevronRight className="w-5 h-5 ml-1" />
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white font-['Poppins'] font-semibold rounded-full hover:bg-white/10 transition-all"
              data-testid="hero-story-btn"
            >
              Our Story
            </a>
          </motion.div>
        </div>
      </div>

      {/* Navigation - Only dots (no arrows) */}
      {multipleSlides && (
        <>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
            {carouselImages.map((img, index) => (
              <button
                key={img.id ?? index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 h-2 bg-[#D4AF37] rounded-full'
                    : 'w-2 h-2 bg-white/50 rounded-full hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-10">
            <motion.div
              key={`progress-${currentIndex}`}
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 5, ease: 'linear' }}
              className="h-full bg-[#D4AF37]"
            />
          </div>

          {/* Swipe hint — fades out */}
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 3, delay: 2 }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/50 text-sm font-['Poppins'] flex items-center gap-2 pointer-events-none z-10"
            data-testid="swipe-hint"
          >
            <span>← Swipe to navigate →</span>
          </motion.div>
        </>
      )}
    </section>
  );
};

export default HeroCarousel;
